import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../shared/notifiers/base-notifier';
import { Ref } from '../../../shared/providers/ref';
import { MapProvider } from '../components/map/map-provider';
import { ScaleState } from './scale-state';
import { ScaleUnitEnum } from '@gishub/domain';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';

@Injectable()
export class ScaleNotifier extends BaseNotifier<ScaleState> {
  private static readonly INITIAL: ScaleState = {
    scale: null,
    unit: ScaleUnitEnum.Metric,
    divisionLabels: [],
    unitLabel: 'km',
    ratio: '',
  };

  private _scaleHandle: { remove(): void } | null = null;

  constructor(ref: Ref) {
    super(ref, ScaleNotifier.INITIAL);
  }

  override build(): void {
    // El view de ArcGIS no existe al construir este notifier (se crea después,
    // en MapNotifier). Por eso el watch sobre `view.scale` debe registrarse de
    // forma reactiva cuando el view aparezca, no una sola vez en build().
    let boundView: any = null;
    this.listen(() => {
      const state = this.ref.watch(MapProvider).state();
      const view = state.view && !state.view.destroyed ? state.view : null;
      if (view === boundView) return;
      this._scaleHandle?.remove();
      boundView = view;
      if (!view) return;
      this._scaleHandle = reactiveUtils.watch(
        () => view.scale,
        (scale: number) => {
          this.updateState(s => ({ ...s, scale }));
          this.computeDivisions();
        },
        { initial: true },
      );
    });
  }

  cycleUnit(): void {
    const current = this.snapshot().unit;
    const next = current === ScaleUnitEnum.Metric ? ScaleUnitEnum.Imperial : ScaleUnitEnum.Metric;
    this.setState({ ...this.snapshot(), unit: next });
    this.computeDivisions();
  }

  private computeDivisions(): void {
    const s = this.snapshot();
    if (!s.scale) return;
    const isMetric = s.unit === ScaleUnitEnum.Metric;
    const d = this.calculateDivisions(s.scale, isMetric);
    this.updateState(state => ({
      ...state,
      divisionLabels: d.map(x => x.label),
      unitLabel: isMetric ? 'km' : 'mi',
      ratio: `1:${Math.round(s.scale ?? 0).toLocaleString()}`,
    }));
  }

  private calculateDivisions(scale: number, metric: boolean): { value: number; label: string; pixels: number }[] {
    const dpi = 96;
    const inchesPerMeter = 39.37;
    const groundRes = scale / (dpi * inchesPerMeter);
    const metersPerPixel = metric ? groundRes : groundRes * 1609.34;

    const niceNumbers = metric
      ? [100000000, 50000000, 20000000, 10000000, 5000000, 2000000, 1000000, 500000, 200000, 100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1]
      : [100000000, 50000000, 20000000, 10000000, 5000000, 2000000, 1000000, 500000, 200000, 100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1];

    const pixelLen = 200;
    const targetGround = pixelLen * metersPerPixel * (metric ? 1 : 1);

    let division = niceNumbers.find(n => targetGround / n >= 1) ?? niceNumbers[niceNumbers.length - 1];
    if (metric) {
      if (division >= 1000) {
        return [{ value: division / 1000, label: `${division / 1000}`, pixels: (division / metersPerPixel) * (pixelLen / targetGround) }];
      }
      return [{ value: division, label: `${division}`, pixels: (division / metersPerPixel) * (pixelLen / targetGround) }];
    }
    return [{ value: division / 1609.34, label: `${(division / 1609.34).toFixed(1)}`, pixels: (division / metersPerPixel / 1609.34) * (pixelLen / targetGround) }];
  }

  protected override onDispose(): void {
    this._scaleHandle?.remove();
    this._scaleHandle = null;
  }
}
